import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sparkles, Loader2, Wand2, ImagePlus, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';

const MODES = [
  { value: 'business', label: 'Business — sell the offer' },
  { value: 'personal', label: 'Personal Brand — thought leadership' },
  { value: 'hybrid', label: 'Hybrid — blend offer + voice' },
];

export default function MediaPackGenerator({ opportunityId = null, defaultTopic = '', onGenerated, onUsage, compact = false }) {
  const api = useApi();
  const [platforms, setPlatforms] = useState([]);
  const [usage, setUsage] = useState(null);
  const [selected, setSelected] = useState(['linkedin', 'instagram', 'video_script']);
  const [mode, setMode] = useState('business');
  const [topic, setTopic] = useState(defaultTopic);
  const [publicSafe, setPublicSafe] = useState(true);
  const [generateImage, setGenerateImage] = useState(false);
  const [imageUploadId, setImageUploadId] = useState(null);
  const [logoUploadId, setLogoUploadId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const h = await api.get('/media-studio/health');
        setPlatforms(h.platforms || []);
      } catch (e) { /* silent */ }
      try {
        const u = await api.get('/media-studio/usage');
        setUsage(u);
        onUsage?.(u);
      } catch (e) { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const atCap = usage != null && usage.remaining <= 0;
  const resetFull = usage?.reset_at
    ? `midnight UTC (${new Date(usage.reset_at).toUTCString().slice(0, 16)})`
    : 'midnight UTC';

  const togglePlatform = (key) => {
    setSelected((prev) => prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]);
  };

  const uploadAsset = async (file, kind, setter) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    fd.append('label', kind === 'exact' ? 'Base image' : 'Logo');
    setUploading(true);
    try {
      const res = await api.upload('/media-studio/uploads', fd);
      setter(res.id);
      toast.success(`${kind === 'exact' ? 'Image' : 'Logo'} uploaded`);
    } catch (e) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const generate = async () => {
    if (atCap) { toast.error('Daily pack limit reached'); return; }
    if (selected.length === 0) { toast.error('Pick at least one platform'); return; }
    if (!opportunityId && !topic.trim() && mode === 'business') {
      toast.error('Add a topic or angle to generate from');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/media-studio/packs/generate', {
        mode,
        platforms: selected,
        topic: topic.trim(),
        opportunity_id: opportunityId,
        public_safe: publicSafe,
        generate_image: generateImage,
        image_upload_id: imageUploadId,
        logo_upload_id: logoUploadId,
      });
      const jobId = res?.job_id;
      if (!jobId) throw new Error('Could not start generation');
      if (res?.usage) { setUsage(res.usage); onUsage?.(res.usage); }

      // Poll the async job — generation runs server-side (many model calls) and
      // would otherwise exceed the edge timeout on a single request.
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      const started = Date.now();
      const MAX_MS = 8 * 60 * 1000;
      let consecutiveErrors = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await sleep(3000);
        let job;
        try {
          job = await api.get(`/media-studio/packs/jobs/${jobId}`);
          consecutiveErrors = 0;
        } catch (e) {
          if (++consecutiveErrors >= 5) throw new Error('Lost connection while generating — check My Packs shortly.');
          continue;
        }
        if (job?.status === 'done') {
          if (job.usage) { setUsage(job.usage); onUsage?.(job.usage); }
          if (job?.usage?.soft_warn) {
            toast.warning(`Heads up — ${job.usage.used_today}/${job.usage.cap} packs used today.`);
          } else {
            toast.success('Media pack generated');
          }
          onGenerated?.(job.pack);
          break;
        }
        if (job?.status === 'error') throw new Error(job.error || 'Generation failed');
        if (Date.now() - started > MAX_MS) throw new Error('Generation is taking longer than expected — check My Packs shortly.');
      }
    } catch (e) {
      const raw = e?.data?.detail;
      let msg = (raw && (raw.message || raw)) || e.message || 'Generation failed';
      if (typeof msg !== 'string' || /<!DOCTYPE|<html|cloudflare/i.test(msg)) {
        msg = 'Generation failed. Please try again in a moment.';
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-white/80 backdrop-blur border-[#E8E6E1]" data-testid="media-pack-generator">
      {!compact && (
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#B89B5E]/10"><Wand2 className="h-5 w-5 text-[#B89B5E]" /></div>
            <CardTitle className="text-lg">Create a Media Pack</CardTitle>
          </div>
        </CardHeader>
      )}
      <CardContent className="space-y-5 pt-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="ms-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <div className="flex items-center gap-2 rounded-md border border-[#E8E6E1] px-3 py-2 w-full">
              <Shield className="h-4 w-4 text-[#B89B5E]" />
              <div className="flex-1">
                <p className="text-xs font-medium text-[#1A1A1A]">Public-safe</p>
                <p className="text-[10px] text-muted-foreground">Hide any prospect names/contacts</p>
              </div>
              <Switch checked={publicSafe} onCheckedChange={setPublicSafe} data-testid="ms-public-safe-toggle" />
            </div>
          </div>
        </div>

        <div>
          <Label className="text-xs">Platforms</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {platforms.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => togglePlatform(p.key)}
                data-testid={`ms-platform-${p.key}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${selected.includes(p.key) ? 'bg-[#B89B5E] text-white border-[#B89B5E]' : 'bg-white text-stone-600 border-[#E8E6E1] hover:border-[#B89B5E]'}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-xs">Topic / Angle {opportunityId && <span className="text-muted-foreground">(optional — opportunity context is used)</span>}</Label>
          <Textarea
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="What's the message or campaign angle?"
            rows={2}
            data-testid="ms-topic-input"
          />
        </div>

        <div className="rounded-md border border-[#E8E6E1] p-3 space-y-3">
          <div className="flex items-center gap-2">
            <ImagePlus className="h-4 w-4 text-[#B89B5E]" />
            <span className="text-xs font-medium text-[#1A1A1A] flex-1">Branded image card</span>
            <Switch checked={generateImage} onCheckedChange={setGenerateImage} data-testid="ms-image-toggle" />
          </div>
          {generateImage && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] text-muted-foreground">Base image (exact — preserved)</Label>
                <input type="file" accept="image/*" data-testid="ms-image-upload"
                  onChange={(e) => uploadAsset(e.target.files?.[0], 'exact', setImageUploadId)}
                  className="block w-full text-xs mt-1" />
                {imageUploadId && <span className="text-[10px] text-emerald-600">✓ uploaded</span>}
              </div>
              <div>
                <Label className="text-[11px] text-muted-foreground">Logo (exact — composited as-is)</Label>
                <input type="file" accept="image/*" data-testid="ms-logo-upload"
                  onChange={(e) => uploadAsset(e.target.files?.[0], 'exact', setLogoUploadId)}
                  className="block w-full text-xs mt-1" />
                {logoUploadId && <span className="text-[10px] text-emerald-600">✓ uploaded</span>}
              </div>
            </div>
          )}
        </div>

        {atCap && (
          <div data-testid="ms-cap-message" className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            You've reached your daily Media Studio limit of {usage.cap} packs. Your quota resets at {resetFull}.
          </div>
        )}

        <Button onClick={generate} disabled={loading || uploading || atCap} className="w-full bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white disabled:opacity-60" data-testid="ms-generate-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
          {loading ? 'Generating…' : 'Generate Media Pack'}
        </Button>

        {loading && (
          <div data-testid="ms-progress" className="rounded-md border border-[#E8E6E1] bg-[#FAF9F7] p-3 text-xs text-stone-600 flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-[#B89B5E]" />
            <span>Writing and reviewing your posts… this can take up to a minute. It will also appear under <span className="font-medium text-[#1A1A1A]">My Packs</span>.</span>
          </div>
        )}

        {usage && (
          <p data-testid="ms-usage-meter" className="text-center text-xs text-muted-foreground">
            <span data-testid="ms-usage-count">{usage.used_today} of {usage.cap} packs today</span> · resets at midnight UTC
          </p>
        )}
      </CardContent>
    </Card>
  );
}
